import { defineMessages, type MessageDescriptor } from 'react-intl';

import type { TranslationKey } from '@heidi/runtime/i18n';
import type { LucideIcon } from 'lucide-react';
import { Calculator, FileText, Pill, Stethoscope, Tablets } from 'lucide-react';

import type { EvidenceVerbCardId } from './evidence-verb-suggestions.types';

/**
 * Direction C on the OUT-OF-SESSION Work homepage research card.
 *
 * The taxonomy is the design doc's **Direction C** — "the finalised starter
 * taxonomy for variant D", 26 level-2 items across 5 level-1 cards. The doc cuts
 * it for in-session and leaves Q18 open; the ruling for this card is that the
 * same taxonomy serves the out-of-session homepage in evidence-only mode, so
 * "in-session only" is not a constraint here.
 *
 * **This is a FORK, not a shared set.** `EvidenceHomepageVerbs` is untouched:
 * in-session arm C ("reused starters") lifts that catalogue as-is, submission
 * events carry no experiment-arm property, and recutting it underneath arm C
 * would pool two treatments under one arm id with nothing in analysis able to
 * separate them. The in-session recut (arm D) lives in
 * `src/components/ui/session-ask-heidi/variants/in-session-verb-starters.ts`
 * and stays the authority for that surface.
 *
 * **Why the two recuts are not one module.** They share the card set, the item
 * ids and most labels, but they differ in the field that matters on each
 * surface: in-session inserts a whole sentence the consult completes ("Work-up
 * for this patient"), out-of-session inserts an open stem the clinician
 * completes ("Workup for "). Arm D's file is on a live experiment path with a
 * suite that pins its reuse count row by row, so the skeleton was left where it
 * is rather than rewritten underneath a running arm.
 *
 * **Card order is the reference-desk order, not the doc's canonical one.** The
 * doc tabulates Work up first; §6 splits the intent mix by surface, and
 * out-of-session behaves as a reference desk where in-session behaves as a
 * reasoning aid. So Guidelines leads here — which is also the card the research
 * surface already opens pre-expanded, since that card keeps the `lookUp` id.
 *
 * **Ids are frozen even where the label moved.** Guidelines keeps `lookUp` and
 * Management keeps `treat`, so the L1-to-L2 conversion baseline still joins and
 * `chip_label` / `chip_category` stay continuous. `medications` and `scores` are
 * already carried by `EvidenceVerb` and `verbToAnalyticsVerb`.
 *
 * **The card carries these five and nothing else.** It used to append the
 * catalogue's Prepare and Write, which made seven level-1 chips and wrapped the
 * row onto two lines; the ruling is that seven is too many, so the object
 * families are not on this card at all.
 *
 * Copy is react-intl. Two rows whose shipped catalogue string still says exactly
 * the right thing carry a `reuseFrom` pointer and resolve through the
 * Crowdin-owned i18next catalogue, which cannot take new keys — through the
 * catalogue row's retrieval (`nonUsVariant`) copy wherever it has one.
 *
 * **Every label is a RETRIEVAL verb — Find, Look up, Check, Compare, Choose.**
 * A clinical ACTION verb (Generate, Interpret, Rule out, Escalate, Treat,
 * Taper) appears in neither the label nor the text this card inserts; it may
 * only appear in what the clinician types after the stem. A row's three
 * strings agree, so a retrieval label cannot sit over an advisory question.
 * That is what makes one copy set serve every market, and it is why eight rows
 * that once pointed at the catalogue now carry their own descriptors: the
 * shipped string asks the advisory question and this card does not.
 */

/**
 * One level-2 row.
 *
 * `reuseFrom` names a `EvidenceHomepageVerbs` item id whose i18next copy stands
 * in for anything this row does not override. A row without it must carry all
 * three of its own descriptors: `resolveRecutItem` drops a row it cannot fill,
 * and the suite resolves every row so that drop fails the file instead.
 */
export type WorkHomeVerbItem = {
  /** Direction C's L2 key. Diverges from `reuseFrom` where the card moved. */
  id: string;
  reuseFrom?: string;
  patternLabel?: MessageDescriptor;
  hoverPreview?: MessageDescriptor;
  starter?: MessageDescriptor;
};

export type WorkHomeVerb = {
  id: EvidenceVerbCardId;
  icon: LucideIcon;
  /** A card that kept its homepage wording keeps its i18next key with it. */
  label: { kind: 'reuse'; key: TranslationKey } | { kind: 'new'; message: MessageDescriptor };
  items: WorkHomeVerbItem[];
};

const verbLabels = defineMessages({
  guidelines: {
    defaultMessage: 'Guidelines',
    id: 'AJnPxXvUbu',
    description:
      'Work home research card: level-1 chip opening guideline and criteria starters',
  },
  management: {
    defaultMessage: 'Management',
    id: 'J22X350H6g',
    description:
      'Work home research card: level-1 chip opening treatment and escalation starters',
  },
  medications: {
    defaultMessage: 'Medications',
    id: '8KfGNKdmr5',
    description:
      'Work home research card: level-1 chip opening drug dosing and safety starters',
  },
  calculate: {
    defaultMessage: 'Calculate',
    id: 'gLkGsZsJoS',
    description:
      'Work home research card: level-1 chip opening clinical scoring-tool starters',
  },
});

const itemLabels = defineMessages({
  compareGuidelines: {
    defaultMessage: 'Compare guideline recommendations',
    id: 'C7ewq2wvzX',
    description: 'Work home research starter for contrasting two guidelines',
  },
  diagnosticCriteria: {
    defaultMessage: 'Find diagnostic criteria',
    id: 'OWazG+ji91',
    description: 'Work home research starter for looking up formal diagnostic criteria',
  },
  referralCriteria: {
    defaultMessage: 'Check referral criteria',
    id: 'MYUbwKHX3i',
    description: 'Work home research starter for when a patient meets referral thresholds',
  },
  eligibility: {
    defaultMessage: 'Check eligibility or funding criteria',
    id: '1uo2lvIbnW',
    description:
      'Work home research starter for who qualifies for a programme or funding',
  },
  chooseInvestigations: {
    defaultMessage: 'Find recommended tests for a presentation',
    id: 'UQhjSmsAYN',
    description:
      'Work home research starter for the tests guidelines recommend for a presentation',
  },
  ruleOutRedFlags: {
    defaultMessage: 'Find red flags for a presentation',
    id: 'JSNdRENXDb',
    description: 'Work home research starter for the red flags described for a presentation',
  },
  prognosis: {
    defaultMessage: 'Check the likely prognosis',
    id: '8+AZGVxyVM',
    description: 'Work home research starter for the expected course of a condition',
  },
  firstLineTreatment: {
    defaultMessage: 'Find a first-line treatment',
    id: 'KC/FaBxSRK',
    description: 'Work home research starter for the first-line treatment of a condition',
  },
  acuteTreatment: {
    defaultMessage: 'Look up acute management guidance',
    id: 'DVD+k7X6sn',
    description: 'Work home research starter for published acute management guidance',
  },
  escalateTreatment: {
    defaultMessage: 'Check what guidelines recommend after an inadequate response',
    id: 'ScTi6TLXLk',
    description:
      'Work home research starter for what guidelines recommend when first-line treatment is not enough',
  },
  drugSafety: {
    defaultMessage: 'Check drug safety or cautions',
    id: 'E5+ytRkqDy',
    description: 'Work home research starter for whether a drug is safe to use',
  },
  interactions: {
    defaultMessage: 'Check drug interactions',
    id: 'tP7Izexaf4',
    description: 'Work home research starter for medication interaction checks',
  },
  polypharmacy: {
    defaultMessage: 'Check polypharmacy evidence',
    id: 'JXptA2hL08',
    description: 'Work home research starter for the evidence on a full medication list',
  },
  monitoring: {
    defaultMessage: 'Check what monitoring is needed',
    id: 'jOqoMloivq',
    description:
      'Work home research starter for the monitoring a treatment or condition needs',
  },
  chooseScore: {
    defaultMessage: 'Choose the right score',
    id: 'Rb3M7wqg/a',
    description: 'Work home research starter for selecting an applicable scoring tool',
  },
  interpretScore: {
    defaultMessage: 'Look up what a score means',
    id: 'HCasWIIXRG',
    description: 'Work home research starter for what a score value indicates',
  },
  stratifyRisk: {
    defaultMessage: 'Stratify risk with a clinical score',
    id: 'G6szK9GO5G',
    description:
      'Work home research starter for placing risk in a band with a clinical score',
  },
  stageCondition: {
    defaultMessage: 'Find staging or classification criteria',
    id: 'BMdfyzkCok',
    description:
      'Work home research starter for the published staging or classification criteria of a condition',
  },
  differential: {
    defaultMessage: 'Look up differential diagnoses',
    id: 'Qzh52xHw8N',
    description: 'Work home research starter for the differential diagnoses of a presentation',
  },
  interpretResults: {
    defaultMessage: 'Understand test results',
    id: 'e7mHiPXU3D',
    description: 'Work home research starter for what test results typically indicate',
  },
  diagnosticWorkup: {
    defaultMessage: 'Look up the standard workup',
    id: 'VeTqNPTz87',
    description: 'Work home research starter for the standard workup of a presentation',
  },
  tapering: {
    defaultMessage: 'Find tapering guidance for a medication',
    id: 'Zcf/v7iEHu',
    description: 'Work home research starter for published tapering guidance',
  },
  dosing: {
    defaultMessage: 'Look up dosing for a medication',
    id: '4CJ478luhT',
    description: 'Work home research starter for the standard dosing of a drug',
  },
  compareTreatments: {
    defaultMessage: 'Compare treatment evidence',
    id: 'bjri26edzq',
    description: 'Work home research starter for comparing the evidence behind two drugs',
  },
});

/**
 * Hover previews: the finished question, with the missing pieces in brackets —
 * the catalogue's own convention.
 *
 * Every one of them asks what a source says rather than what the clinician
 * should do, and none names `[patient details]`: there is no patient on this
 * surface to bind one to.
 */
const previews = defineMessages({
  diagnosticWorkup: {
    defaultMessage: 'What is the standard workup for [symptom or presentation]?',
    id: 'g9tcUyq9/N',
    description: 'Work home research starter preview: the standard workup for a presentation',
  },
  chooseInvestigations: {
    defaultMessage: 'What tests are recommended for [presentation]?',
    id: 'PMNgMwYjOY',
    description: 'Work home research starter preview: the tests recommended for a presentation',
  },
  ruleOutRedFlags: {
    defaultMessage: 'Red flags described for [presentation]?',
    id: 'KWd1ofJ59F',
    description: 'Work home research starter preview: the red flags described for a presentation',
  },
  prognosis: {
    defaultMessage: 'What does the evidence say about prognosis in [condition]?',
    id: 'FdkV4e8O0y',
    description: 'Work home research starter preview: the evidence on prognosis',
  },
  firstLineTreatment: {
    defaultMessage: 'What do guidelines recommend as first-line for [condition]?',
    id: 'Dl6YzTD73k',
    description: 'Work home research starter preview: the guideline first-line treatment',
  },
  acuteTreatment: {
    defaultMessage: 'What is the standard management for acute [condition]?',
    id: 'owOVxFfqAF',
    description: 'Work home research starter preview: standard acute management',
  },
  escalateTreatment: {
    defaultMessage:
      'What do guidelines and the literature recommend after an inadequate response to [treatment]?',
    id: 'EN9dBRFsEl',
    description:
      'Work home research starter preview: what is recommended after an inadequate response',
  },
  compareGuidelines: {
    defaultMessage: 'How do guidelines differ on [clinical topic]?',
    id: 'LAR87/OlbB',
    description: 'Work home research starter preview: compare guidelines',
  },
  diagnosticCriteria: {
    defaultMessage: 'Diagnostic criteria for [condition]',
    id: 'l7r+Efc4bw',
    description: 'Work home research starter preview: diagnostic criteria',
  },
  referralCriteria: {
    defaultMessage: 'What are the referral criteria for [condition]?',
    id: 'DTW1MwWau2',
    description: 'Work home research starter preview: referral criteria',
  },
  eligibility: {
    defaultMessage: 'Who qualifies for [program or funding]?',
    id: '4YNrC9kMoE',
    description: 'Work home research starter preview: eligibility or funding criteria',
  },
  dosing: {
    defaultMessage: 'What is the standard dosing of [drug]?',
    id: 'ro8ZJ9m5ZG',
    description: 'Work home research starter preview: the standard dosing of a drug',
  },
  interactions: {
    defaultMessage: 'Interactions between [drug] and [drug]?',
    id: 'ytWHcYn0bY',
    description: 'Work home research starter preview: drug interactions',
  },
  polypharmacy: {
    defaultMessage: 'What does the evidence say about polypharmacy in [medication list]?',
    id: 'neZ62+CWKO',
    description: 'Work home research starter preview: the evidence on polypharmacy',
  },
  monitoring: {
    defaultMessage:
      'What monitoring do guidelines recommend for [drug or condition], and how often?',
    id: 'K5ItgC4VQX',
    description: 'Work home research starter preview: the monitoring guidelines recommend',
  },
  chooseScore: {
    defaultMessage: 'Which score applies to [presentation]?',
    id: 'P8o/3y1JrQ',
    description: 'Work home research starter preview: choose the right score',
  },
  interpretScore: {
    defaultMessage: 'What does a [score] of [value] indicate in [presentation]?',
    id: 'bz/5soy6qe',
    description: 'Work home research starter preview: what a score value indicates',
  },
  stratifyRisk: {
    defaultMessage: 'Stratify risk for [presentation] using a clinical score',
    id: 'ZfswSNCdV2',
    description: 'Work home research starter preview: stratify risk with a clinical score',
  },
  stageCondition: {
    defaultMessage: 'What staging criteria apply to [condition]?',
    id: 'LmOmveHUEh',
    description: 'Work home research starter preview: the staging criteria for a condition',
  },
  differential: {
    defaultMessage: 'Differential diagnoses for [symptom or presentation]?',
    id: '9TFD/Dybrh',
    description: 'Work home research starter preview: the differential diagnoses',
  },
  interpretResults: {
    defaultMessage: 'What do results of [test] showing [values] typically indicate?',
    id: 'JxK3MRWe7E',
    description: 'Work home research starter preview: what test results typically indicate',
  },
  tapering: {
    defaultMessage: 'What is the tapering schedule for [medication]?',
    id: 'rfVKM/aNnL',
    description: 'Work home research starter preview: the tapering schedule for a medication',
  },
  compareTreatments: {
    defaultMessage: 'Compare the evidence for [drug A] vs [drug B] for [condition]?',
    id: 'SonbOLHgfM',
    description: 'Work home research starter preview: compare the evidence behind two drugs',
  },
  drugSafety: {
    defaultMessage:
      'What are the documented cautions for [drug] with [medication or condition]?',
    id: '26uLTMfpz2',
    description: 'Work home research starter preview: the documented cautions for a drug',
  },
});

/**
 * What lands in the composer. Every one of these ends mid-sentence.
 *
 * That is the whole out-of-session adaptation: the doc gives 21 of its 26 rows
 * as complete in-session sentences ("Interactions between this patient's
 * medications"), and a complete sentence takes the autocomplete trigger away —
 * the doc says so itself. Stripping the patient referent leaves a stem, the stem
 * opens the typeahead, and autocomplete-as-L3 survives the port.
 */
const starters = defineMessages({
  chooseInvestigations: {
    defaultMessage: 'What tests are recommended for ',
    id: 'yaBqyv1U4R',
    description: 'Composer text inserted by the Work home recommended-tests starter',
  },
  ruleOutRedFlags: {
    defaultMessage: 'Red flags described for ',
    id: 'WmPUhyD6V9',
    description: 'Composer text inserted by the Work home red-flags starter',
  },
  prognosis: {
    defaultMessage: 'What does the evidence say about prognosis in ',
    id: 'bRk0VeMZ99',
    description: 'Composer text inserted by the Work home prognosis starter',
  },
  firstLineTreatment: {
    defaultMessage: 'What do guidelines recommend as first-line for ',
    id: 'FirUOFeQZI',
    description: 'Composer text inserted by the Work home first-line treatment starter',
  },
  escalateTreatment: {
    defaultMessage: 'What do guidelines and the literature recommend after an inadequate response to ',
    id: 'y6aJyLyhWM',
    description:
      'Composer text inserted by the Work home inadequate-response starter',
  },
  compareGuidelines: {
    defaultMessage: 'How do guidelines differ on ',
    id: 'jeXb7PvVcn',
    description: 'Composer text inserted by the Work home compare-guidelines starter',
  },
  diagnosticCriteria: {
    defaultMessage: 'Diagnostic criteria for ',
    id: 'K/jBuzuaGH',
    description: 'Composer text inserted by the Work home diagnostic-criteria starter',
  },
  referralCriteria: {
    defaultMessage: 'What are the referral criteria for ',
    id: 'UeKjRRhDM+',
    description: 'Composer text inserted by the Work home referral-criteria starter',
  },
  eligibility: {
    defaultMessage: 'Who qualifies for ',
    id: 'Sc7L9Lmi8D',
    description: 'Composer text inserted by the Work home eligibility starter',
  },
  interactions: {
    defaultMessage: 'Interactions between ',
    id: 'VhiNagFYBS',
    description: 'Composer text inserted by the Work home drug-interactions starter',
  },
  polypharmacy: {
    defaultMessage: 'What does the evidence say about polypharmacy in ',
    id: 'cmii3ngUtd',
    description: 'Composer text inserted by the Work home polypharmacy starter',
  },
  monitoring: {
    defaultMessage: 'What monitoring do guidelines recommend for ',
    id: 'zfMXKiUR1l',
    description: 'Composer text inserted by the Work home monitoring starter',
  },
  chooseScore: {
    defaultMessage: 'Which score applies to ',
    id: 'nVdXbodPwz',
    description: 'Composer text inserted by the Work home choose-a-score starter',
  },
  interpretScore: {
    defaultMessage: 'What does a ',
    id: 'pIqiOy5Z4m',
    description: 'Composer text inserted by the Work home score-meaning starter',
  },
  stratifyRisk: {
    defaultMessage: 'Stratify risk using a clinical score for ',
    id: 'O5OQF5lw4l',
    description: 'Composer text inserted by the Work home risk-stratification starter',
  },
  stageCondition: {
    defaultMessage: 'What staging criteria apply to ',
    id: '/6y4n+NVnU',
    description: 'Composer text inserted by the Work home staging-criteria starter',
  },
  differential: {
    defaultMessage: 'Differential diagnosis for ',
    id: 'KEsj3gxMt8',
    description: 'Composer text inserted by the Work home differential starter',
  },
  interpretResults: {
    defaultMessage: 'What do results of ',
    id: 'Pv4UJe1XpI',
    description: 'Composer text inserted by the Work home test-results starter',
  },
  diagnosticWorkup: {
    defaultMessage: 'What is the standard workup for ',
    id: 'W5WGPUlUlg',
    description: 'Composer text inserted by the Work home standard-workup starter',
  },
  acuteTreatment: {
    defaultMessage: 'What is the standard management for acute ',
    id: 'TNUSkwd5C8',
    description: 'Composer text inserted by the Work home acute-management starter',
  },
  tapering: {
    defaultMessage: 'What is the tapering schedule for ',
    id: 'kootfx44v6',
    description: 'Composer text inserted by the Work home tapering-schedule starter',
  },
  dosing: {
    defaultMessage: 'What is the standard dosing of ',
    id: 'i5jWmA5R/1',
    description: 'Composer text inserted by the Work home dosing starter',
  },
  compareTreatments: {
    defaultMessage: 'Compare the evidence for ',
    id: 'dhrtwWqigA',
    description: 'Composer text inserted by the Work home compare-evidence starter',
  },
  drugSafety: {
    defaultMessage: 'What are the documented cautions for ',
    id: 'PKf6B+y0wC',
    description: 'Composer text inserted by the Work home drug-cautions starter',
  },
});

/**
 * Direction C, reference-desk order: 5 · 6 · 4 · 6 · 5.
 *
 * Management carries four because the review collapsed step-up and add-on
 * therapy into one escalation item — a clinician does not arrive knowing which
 * of the two they want, and that decision is the thing they came to ask about.
 */
export const WORK_HOME_RECUT_VERBS: WorkHomeVerb[] = [
  {
    // "Guidelines", not "Look up" — four of its five rows are criteria lookups,
    // so it reads as a document card rather than a search one. Icon follows the
    // label from the search glyph to a document one.
    id: 'lookUp',
    icon: FileText,
    label: { kind: 'new', message: verbLabels.guidelines },
    items: [
      { id: 'lookUp.guideline', reuseFrom: 'lookUp.guideline' },
      {
        id: 'lookUp.compareGuidelines',
        patternLabel: itemLabels.compareGuidelines,
        hoverPreview: previews.compareGuidelines,
        starter: starters.compareGuidelines,
      },
      {
        id: 'lookUp.diagnosticCriteria',
        patternLabel: itemLabels.diagnosticCriteria,
        hoverPreview: previews.diagnosticCriteria,
        starter: starters.diagnosticCriteria,
      },
      {
        id: 'lookUp.referralCriteria',
        patternLabel: itemLabels.referralCriteria,
        hoverPreview: previews.referralCriteria,
        starter: starters.referralCriteria,
      },
      {
        // The doc's own stem asks whether "this patient" qualifies, and nothing
        // out of session binds that — so the row asks who qualifies instead.
        id: 'lookUp.eligibility',
        patternLabel: itemLabels.eligibility,
        hoverPreview: previews.eligibility,
        starter: starters.eligibility,
      },
    ],
  },
  {
    id: 'workUp',
    icon: Stethoscope,
    label: { kind: 'reuse', key: 'chat.verbs.workUp' },
    items: [
      {
        id: 'workUp.diagnosticWorkup',
        patternLabel: itemLabels.diagnosticWorkup,
        hoverPreview: previews.diagnosticWorkup,
        starter: starters.diagnosticWorkup,
      },
      {
        id: 'workUp.differential',
        patternLabel: itemLabels.differential,
        hoverPreview: previews.differential,
        starter: starters.differential,
      },
      {
        id: 'workUp.chooseInvestigations',
        patternLabel: itemLabels.chooseInvestigations,
        hoverPreview: previews.chooseInvestigations,
        starter: starters.chooseInvestigations,
      },
      {
        id: 'workUp.interpretResults',
        patternLabel: itemLabels.interpretResults,
        hoverPreview: previews.interpretResults,
        starter: starters.interpretResults,
      },
      {
        id: 'workUp.ruleOutRedFlags',
        patternLabel: itemLabels.ruleOutRedFlags,
        hoverPreview: previews.ruleOutRedFlags,
        starter: starters.ruleOutRedFlags,
      },
      {
        id: 'workUp.prognosis',
        patternLabel: itemLabels.prognosis,
        hoverPreview: previews.prognosis,
        starter: starters.prognosis,
      },
    ],
  },
  {
    // "Management", not "Treat": compliance ruled that "treat" and "treatment"
    // cannot be card labels. The id stays `treat` for the analytics join.
    id: 'treat',
    icon: Pill,
    label: { kind: 'new', message: verbLabels.management },
    items: [
      {
        id: 'treat.firstLineMedication',
        patternLabel: itemLabels.firstLineTreatment,
        hoverPreview: previews.firstLineTreatment,
        starter: starters.firstLineTreatment,
      },
      {
        id: 'treat.compareTreatments',
        patternLabel: itemLabels.compareTreatments,
        hoverPreview: previews.compareTreatments,
        starter: starters.compareTreatments,
      },
      {
        id: 'treat.acuteTreatment',
        patternLabel: itemLabels.acuteTreatment,
        hoverPreview: previews.acuteTreatment,
        starter: starters.acuteTreatment,
      },
      {
        id: 'treat.escalateTreatment',
        patternLabel: itemLabels.escalateTreatment,
        hoverPreview: previews.escalateTreatment,
        starter: starters.escalateTreatment,
      },
    ],
  },
  {
    // Dosing, safety and tapering were always Medications asks mis-filed under
    // Look up.
    id: 'medications',
    icon: Tablets,
    label: { kind: 'new', message: verbLabels.medications },
    items: [
      {
        id: 'medications.dosing',
        patternLabel: itemLabels.dosing,
        hoverPreview: previews.dosing,
        starter: starters.dosing,
      },
      {
        id: 'medications.interactions',
        patternLabel: itemLabels.interactions,
        hoverPreview: previews.interactions,
        starter: starters.interactions,
      },
      {
        id: 'medications.drugSafety',
        patternLabel: itemLabels.drugSafety,
        hoverPreview: previews.drugSafety,
        starter: starters.drugSafety,
      },
      {
        id: 'medications.tapering',
        patternLabel: itemLabels.tapering,
        hoverPreview: previews.tapering,
        starter: starters.tapering,
      },
      {
        id: 'medications.polypharmacy',
        patternLabel: itemLabels.polypharmacy,
        hoverPreview: previews.polypharmacy,
        starter: starters.polypharmacy,
      },
      {
        id: 'medications.monitoring',
        patternLabel: itemLabels.monitoring,
        hoverPreview: previews.monitoring,
        starter: starters.monitoring,
      },
    ],
  },
  {
    // "Calculate", not "Scores" — the team's read is that it is clearer. The id
    // stays `scores`, which is what the events are keyed under.
    id: 'scores',
    icon: Calculator,
    label: { kind: 'new', message: verbLabels.calculate },
    items: [
      {
        id: 'scores.chooseScore',
        patternLabel: itemLabels.chooseScore,
        hoverPreview: previews.chooseScore,
        starter: starters.chooseScore,
      },
      { id: 'scores.calculateScore', reuseFrom: 'workUp.calculateScore' },
      {
        id: 'scores.interpretScore',
        patternLabel: itemLabels.interpretScore,
        hoverPreview: previews.interpretScore,
        starter: starters.interpretScore,
      },
      {
        id: 'scores.stratifyRisk',
        patternLabel: itemLabels.stratifyRisk,
        hoverPreview: previews.stratifyRisk,
        starter: starters.stratifyRisk,
      },
      {
        id: 'scores.stageCondition',
        patternLabel: itemLabels.stageCondition,
        hoverPreview: previews.stageCondition,
        starter: starters.stageCondition,
      },
    ],
  },
];
